import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { Add, Cancel } from "@mui/icons-material";
import {
  Autocomplete,
  Chip,
  createFilterOptions,
  Grid,
  InputLabel,
  TextField,
  MenuItem,
  Select,
  Icon,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { useEffect, useRef, useState, useContext } from "react";
import { getColors } from "utils/colors";
import PropTypes from "prop-types";
import SoftBox from "components/CheckBox";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { MessageManager } from "context";
const productoInicial = {
  nombre: "",
  precioCarta: "",
  comentario: "",
  ingredientes: [],
  estado: "Pendiente",
  cantidad: 1,
  seccion: "Cocina",
};
const categoriaInicial = {
  categoria: "",
};
const filter = createFilterOptions();

const AgregarProducto = ({ pedido, setPedido }) => {
  const [producto, setProducto] = useState(productoInicial);
  const [productosOptions, setProductosOptions] = useState([]);
  const [categoria, setCategoria] = useState(categoriaInicial);
  const [disponibilidadOperativaMap, setDisponibilidadOperativaMap] = useState({});
  const inputRef = useRef(null);
  const { handleSnackbar } = useContext(MessageManager);
  const idSucursalStock = Number(pedido?.idSede || 1);

  useEffect(() => {
    setProducto(productoInicial);
    setProductosOptions([]);
  }, []);

  const [getProductos, { data: productos }] = useLazyQuery(
    gql`
      query productos($filter: JSON) {
        productos(filter: $filter) {
          id
          nombre
          precioCarta
          precioOnline
          precioPorMayor
          esElaborado
          cantidadPorMayor
          categoriaProducto {
            categoria
          }
          ingredientes {
            id
          }
          seccion
          menuDelDia {
            precio
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables: {
        filter: { disponibilidad: 1, idCategoria: categoria?.id },
      },
    }
  );

  const { data: stockSucursal } = useQuery(
    gql`
      query getProductos($idSucursal: ID, $filter: JSON) {
        materiasPrimas(filter: $filter) {
          id
          nombre
          idProductoReferencia
          stock(idSucursal: $idSucursal) {
            cantidad
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables: {
        filter: { tipo: "Plato" },
        idSucursal: idSucursalStock,
      },
    }
  );

  const { data: dataCategoria } = useQuery(
    gql`
      query categoriasProducto {
        categoriasProducto {
          id
          categoria
        }
      }
    `
  );

  const [validarDisponibilidadProducto] = useLazyQuery(
    gql`
      query validarDisponibilidadProducto(
        $idProducto: ID!
        $cantidad: Int!
        $esElaborado: Boolean!
        $idSucursal: ID
      ) {
        validarDisponibilidadProducto(
          idProducto: $idProducto
          cantidad: $cantidad
          esElaborado: $esElaborado
          idSucursal: $idSucursal
        ) {
          ok
          mensaje
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  const [validarDisponibilidadProductos] = useLazyQuery(
    gql`
      query validarDisponibilidadProductos(
        $productos: [DisponibilidadProductoBatchInput!]!
        $idSucursal: ID
      ) {
        validarDisponibilidadProductos(productos: $productos, idSucursal: $idSucursal) {
          idProducto
          cantidad
          ok
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    getProductos();
  }, [categoria]);

  useEffect(() => {
    const productosDisponibles = productos?.productos || [];
    if (!categoria?.id || productosDisponibles.length === 0) {
      setDisponibilidadOperativaMap({});
      return;
    }

    const pendientesPorProducto = (pedido?.detalles || []).reduce((acc, detalle) => {
      if (detalle?.estado !== "PENDIENTE") return acc;
      const idProd = Number(detalle?.producto?.id || detalle?.idProducto);
      if (!idProd) return acc;
      acc[idProd] = (acc[idProd] || 0) + Number(detalle?.cantidad || 0);
      return acc;
    }, {});

    const productosBatch = productosDisponibles.map((prod) => {
      const idProd = Number(prod.id);
      const cantidadPendiente = Number(pendientesPorProducto[idProd] || 0);
      return {
        idProducto: idProd,
        cantidad: cantidadPendiente + 1,
        esElaborado: !!prod.esElaborado,
      };
    });

    let isActive = true;
    validarDisponibilidadProductos({
      variables: {
        productos: productosBatch,
        idSucursal: idSucursalStock,
      },
    })
      .then(({ data }) => {
        if (!isActive) return;
        const map = {};
        for (const item of data?.validarDisponibilidadProductos || []) {
          map[Number(item.idProducto)] = !!item.ok;
        }
        setDisponibilidadOperativaMap(map);
      })
      .catch(() => {
        if (!isActive) return;
        setDisponibilidadOperativaMap({});
      });

    return () => {
      isActive = false;
    };
  }, [productos, pedido?.detalles, categoria?.id, idSucursalStock, validarDisponibilidadProductos]);
  //Ver que productos son elaborados y cuales no
  //En caso de ser elaborado, ver si hay stock suficiente
  useEffect(() => {
    if (categoria !== categoriaInicial) {
      if (productos?.productos) {
        // Separar productos elaborados y no elaborados
        let productosElaborados = productos.productos.filter((p) => p.esElaborado);
        let productosNoElaborados = productos.productos.filter((p) => !p.esElaborado);

        // Filtrar productos elaborados con stock mayor a 0 y agregar cantidad de stock
        let productosConStock = productosElaborados
          .map((producto) => {
            let stock = stockSucursal?.materiasPrimas.find(
              (s) => s.idProductoReferencia == producto.id
            );
            const disponibleOperativo = disponibilidadOperativaMap[Number(producto.id)];
            const sinStockOperativo = disponibleOperativo === false;
            return stock
              ? {
                ...producto,
                stockCantidad: stock.stock?.cantidad ?? 0,
                disabledStock: sinStockOperativo,
              }
              : null;
          })
          .filter((p) => p && p.stockCantidad > 0); // Solo incluir si tiene stock

        // Combinar productos no elaborados con los elaborados con stock
        let prodCompletos = [
          ...productosNoElaborados.map((productoNoElaborado) => ({
            ...productoNoElaborado,
            disabledStock: disponibilidadOperativaMap[Number(productoNoElaborado.id)] === false,
          })),
          ...productosConStock,
        ];

        // Actualizar el estado con los productos válidos
        setProductosOptions(prodCompletos);
      }
    } else {
      setProductosOptions([]);
    }
  }, [productos, stockSucursal, disponibilidadOperativaMap]);

  const handleChangeProducto = (e) => {
    setProducto({
      ...producto,
      [e.target.name]: e.target.value,
    });
  };
  const handleAddDetalle = async () => {
    let ingredientesQuitados = producto.ingredientes?.filter((ingrediente) => ingrediente.quitado);
    if (producto?.nombre && producto?.precio && producto?.cantidad) {
      try {
        let productoExistente = (pedido?.detalles || []).findIndex(
          (detalle) =>
            detalle.producto.id === producto.id &&
            detalle.comentario === producto.comentario &&
            detalle.estado === "PENDIENTE" &&
            detalle.precio == producto.precioCarta &&
            (!detalle.idsIngredientesQuitados || ingredientesQuitados?.length === 0) &&
            ingredientesQuitados?.length === 0 &&
            !detalle.numeroComanda
        );

        const cantidadAcumulada =
          productoExistente !== -1
            ? Number(pedido?.detalles?.[productoExistente]?.cantidad || 0) + Number(producto.cantidad)
            : Number(producto.cantidad);

        const { data } = await validarDisponibilidadProducto({
          variables: {
            idProducto: producto.id,
            cantidad: cantidadAcumulada,
            esElaborado: !!producto.esElaborado,
            idSucursal: idSucursalStock,
          },
        });

        if (!data?.validarDisponibilidadProducto?.ok) {
          handleSnackbar(
            data?.validarDisponibilidadProducto?.mensaje || "Stock agotado para este producto.",
            "error"
          );
          inputRef.current.focus();
          return;
        }

        if (productoExistente !== -1) {
          const updatedDetalles = pedido?.detalles?.map((detalle, index) => {
            if (index === productoExistente) {
              return {
                ...detalle,
                cantidad: Number(detalle.cantidad) + Number(producto.cantidad),
              };
            }
            return detalle;
          });
          setPedido({
            ...pedido,
            detalles: updatedDetalles,
          });
          setProducto(productoInicial);
          inputRef.current.focus();
          return;
        }

        let nuevoProducto = {
          idProducto: producto.id,
          producto: {
            nombre: producto.nombre,
            id: producto.id,
          },
          cantidad: Number(producto.cantidad),
          precio: producto.precio,
          comentario: producto.comentario,
          estado: "ENTREGADO",
          idsIngredientesQuitados: ingredientesQuitados.map((ingrediente) => ingrediente.id),
          ingredientesQuitados: ingredientesQuitados,
          seccion: producto.seccion,
          esElaborado: producto.esElaborado,
        };
        setPedido({
          ...pedido,
          detalles: [...pedido.detalles, { ...nuevoProducto }],
        });
        setProducto(productoInicial);
      } catch (error) {
        handleSnackbar("No se pudo validar stock en este momento.", "error");
      }
    }
    inputRef.current.focus();
  };

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (producto?.nombre && producto?.precio && producto?.cantidad) {
      if (
        producto?.cantidad < producto?.cantidadPorMayor &&
        producto?.precio == producto?.precioPorMayor
      ) {
        handleSnackbar(
          "El precio por mayor solo aplica si la cantidad es mayor o igual a la cantidad por mayor",
          "warning"
        );
      }
    }
  }, [producto.cantidad, producto.precio]);
  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <InputLabel htmlFor="categoria">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Categoria
          </SoftTypography>
        </InputLabel>
        <Autocomplete
          value={categoria}
          options={dataCategoria?.categoriasProducto || []}
          getOptionLabel={(option) => option.categoria}
          onChange={(e, newValue) => {
            setCategoria(newValue);
          }}
          renderInput={(params) => <TextField {...params} placeholder="Buscar Categoria" />}
        />
      </Grid>

      <Grid item xs={6}>
        <InputLabel htmlFor="producto">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Producto
          </SoftTypography>
        </InputLabel>
        <Autocomplete
          value={producto}
          options={productosOptions.map((option) => option) || []}
          groupBy={(option) => option.categoriaProducto?.categoria || "Sin categoria"}
          getOptionDisabled={(option) => !!option?.disabledStock}
          getOptionLabel={(option) =>
            option.nombre +
            (option.disabledStock
              ? " - Sin stock"
              : option.stockCantidad
                ? ` - Stock disponible: ${option.stockCantidad}`
                : "")
          }
          noOptionsText="No hay resultados"
          disabled={categoria === categoriaInicial}
          onChange={(e, newValue) => {
            if (newValue) {
              setProducto({
                ...newValue,
                cantidad: 1,
                precio: null,
              });
            } else {
              setProducto(productoInicial);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Buscar Producto" inputRef={inputRef} />
          )}
        />
      </Grid>
      <Grid item xs={2}>
        <Grid>
          <InputLabel htmlFor="cantidad">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Cantidad
            </SoftTypography>
          </InputLabel>
          <SoftInput
            placeholder="Cantidad"
            name="cantidad"
            type="number"
            value={producto?.cantidad}
            onChange={handleChangeProducto}
            error={producto?.cantidad < 1 || producto?.cantidad > producto?.stockCantidad}
          />
        </Grid>
      </Grid>
      <Grid item xs={3}>
        <InputLabel htmlFor="precioCarta">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Precio{" "}
          </SoftTypography>
          <SoftTypography variant="caption" fontWeight="light">
            (Por unidad)
          </SoftTypography>
        </InputLabel>
        {/* <SoftInputNumber
          placeholder="$"
          name="precioCarta"
          value={producto?.precioCarta}
          onChange={handleChangeProducto}
        /> */}
        <Select
          value={producto?.precio}
          onChange={(e) => {
            setProducto({
              ...producto,
              precio: e.target.value,
            });
          }}
          disabled={!producto?.nombre}
        >
          <MenuItem value={producto?.precioCarta}>
            <SoftBox display="flex" alignItems="center">
              <Icon fontSize="medium" color="primary">
                menu_book
              </Icon>
              <SoftTypography variant="button" fontWeight="medium">
                &nbsp; Consumir en el local:{" "}
                <SoftTypography variant="button" fontWeight="bold">
                  $ {producto?.precioCarta ? formatMoneyPunto(producto?.precioCarta) : "0.00"}
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </MenuItem>
          <MenuItem value={producto?.precioOnline}>
            <SoftBox display="flex" alignItems="center">
              <Icon
                fontSize="medium"
                sx={{
                  color: getUproColor("uproGreen"),
                }}
              >
                takeout_dining
              </Icon>
              <SoftTypography variant="button" fontWeight="medium">
                &nbsp; Para llevar:{" "}
                <SoftTypography variant="button" fontWeight="bold">
                  $ {producto?.precioOnline ? formatMoneyPunto(producto?.precioOnline) : "0.00"}
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </MenuItem>
          <MenuItem
            value={producto?.precioPorMayor}
            disabled={producto?.cantidad < producto?.cantidadPorMayor}
          >
            <SoftBox display="flex" alignItems="center">
              <Icon
                fontSize="medium"
                sx={{
                  color: getUproColor("uproYellow"),
                }}
              >
                shopping_cart
              </Icon>
              <SoftTypography variant="button" fontWeight="medium">
                &nbsp; Precio Por Mayor:{" "}
                <SoftTypography variant="button" fontWeight="bold">
                  $ {producto?.precioPorMayor ? formatMoneyPunto(producto?.precioPorMayor) : "0.00"}
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </MenuItem>
        </Select>
      </Grid>
      <Grid
        item
        xs={1}
        display="flex"
        flexDirection="row"
        alignItems="flex-end"
        justifyContent={"flex-end"}
      >
        <SoftButton
          variant="contained"
          fullWidth
          color="primary"
          onClick={handleAddDetalle}
          disabled={
            !producto?.nombre ||
            !producto?.precio ||
            !producto?.cantidad ||
            producto?.disabledStock ||
            (producto?.cantidad < producto?.cantidadPorMayor &&
              producto?.precio == producto?.precioPorMayor) ||
            producto?.cantidad > producto?.stockCantidad
          }
        >
          <Add />
        </SoftButton>
      </Grid>
      {/* <Grid item xs={12}>
        <InputLabel htmlFor="idsIngredientesQuitados">
          <SoftTypography component="label" variant="caption" fontWeight="bold">
            Ingredientes - Quitar
          </SoftTypography>
        </InputLabel>
        <Autocomplete
          disabled={producto?.ingredientes?.length === 0}
          multiple
          disableClearable={true}
          id="tags-filled"
          freeSolo
          options={producto?.ingredientes?.map((option) => option) || []}
          getOptionLabel={(option) => {
            return option.nombre;
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            return filtered;
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  },
                }}
              />
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                label={option.nombre}
                {...getTagProps({ index })}
                style={{
                  backgroundColor: option?.quitado ? getColors("error") : getColors("success"),
                  color: "white",
                }}
                deleteIcon={option?.quitado ? <Add /> : <Cancel />}
              />
            ))
          }
          name="ingredientes"
          value={producto?.ingredientes || []}
          onChange={(e, newValue, selectOption, details) => {
            let listadoIngredientes = producto.ingredientes.filter((ingrediente) =>
              newValue.includes(ingrediente)
            );
            let ingredienteCambiado = {
              ...details.option,
              quitado: !details?.option.quitado,
            };
            listadoIngredientes = [...listadoIngredientes, { ...ingredienteCambiado }];
            setProducto({
              ...producto,
              ingredientes: listadoIngredientes,
            });
          }}
        />
      </Grid> */}
      <Grid item xs={12}>
        <Grid>
          <InputLabel htmlFor="comentario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Comentario adicional
            </SoftTypography>
          </InputLabel>
          <SoftInput
            placeholder="Comentario"
            name="comentario"
            value={producto?.comentario}
            onChange={handleChangeProducto}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AgregarProducto;

AgregarProducto.propTypes = {
  pedido: PropTypes.object,
  setPedido: PropTypes.func,
};
